import React from 'react';
import { Calendar as BigCalendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { setSchedulerSettingActionCreator } from 'plugins/store/reducers/common.reducer';
import schedulerViewMode from 'utils/enums/scheduler-view-mode.enum';

import { getContactCentersList } from 'services/contact-center.service';
import appointmentsMapping from 'mappings/appointmentsMapping';
import Toolbar from './Toolbar';
import Event from './Event';
import withStyles from '@material-ui/core/styles/withStyles';
import styles from 'assets/jss/material-dashboard-pro-react/components/schedulerCalendar';

const HALF_OF_HOURS_IN_MILLISECONDS = 30 * 60 * 1000;

const localizer = momentLocalizer(moment);
localizer.formats.dayHeaderFormat = 'DD MMM, yyyy';
localizer.formats.dayFormat = 'ddd M/D';
localizer.formats.weekdayFormat = 'ddd';

const unselectCallCenter = {
	value: 'all',
	label: 'Select All',
};

class Calendar extends React.Component {
	state = {
		allowRequest: true,
		dateStart: '',
		dateEnd: '',
		contactCenterList: [unselectCallCenter],
		toolbar: null,
	};

	bigCalendar = React.createRef();

	componentDidMount() {
		this.bigCalendar.current.handleViewChange(this.props.view);
		this.getCallCenters();
		this.setToolbarComponent();
	}

	componentDidUpdate(prevProps) {
		if (prevProps.selectedUser !== this.props.selectedUser) {
			this.onChangeSelectedUser();
		}
	}

	setToolbarComponent() {
		const toolbar = (props) => (
			<Toolbar
				{...props}
				selectedUser={this.props.selectedUser}
				createAppointment={this.props.createAppointment}
				createVacation={this.props.createVacation}
				changeView={this.changeView}
				view={this.props.view}
				contactCenterList={this.state.contactCenterList}
				contactCenter={this.props.contactCenter}
				setContactCenter={this.setContactCenter}
				setSelectedUser={this.setSelectedUser}
				toggleViewMode={this.toggleViewMode}
			/>
		);

		this.setState({ toolbar });
	}

	async getCallCenters() {
		try {
			const data = await getContactCentersList();
			const formattedCallCenters = data.map((callCenter) => ({
				...callCenter,
				value: callCenter._id,
				label: callCenter.name,
			}));
			formattedCallCenters.unshift(unselectCallCenter);
			this.setState({ contactCenterList: formattedCallCenters }, () => {
				if (this.props.contactCenter?.value) {
					this.setContactCenter(this.props.contactCenter?.value);
				}
			});
		} catch (error) {
			console.log(error);
		}
	}

	setContactCenter = (contactCenterId) => {
		const contactCenter = this.state.contactCenterList.find((contactCenter) => contactCenter.value === contactCenterId);
		this.props.setSchedulerSetting('contactCenter', contactCenter);
	}

	setSelectedUser = (selectedUser) => {
		this.props.setSchedulerSetting('selectedUser', selectedUser);
	}

	toggleViewMode = () => {
		const viewMode = this.props.viewMode === schedulerViewMode.CALENDAR
			? schedulerViewMode.LIST
			: schedulerViewMode.CALENDAR;
		this.props.setSchedulerSetting('viewMode', viewMode);
	}

	onChangeSelectedUser() {
		if (this.props.getAppointments) {
			this.props.getAppointments({
				from: this.state.dateStart,
				to: this.state.dateEnd,
			});
		}
	}

	onSelectEvent = ({ id, type }) => {
		this.props.editAppointment(id, type);
	}

	onSelectSlot = (event) => {
		if (event.action === 'doubleClick') return;
		if (event.slots?.length === 1) {
			this.props.createAppointment(event.slots[0]);
		}

		if (event.slots?.length === 2) {
			if (Math.abs(event.slots[0] - event.slots[1]) === HALF_OF_HOURS_IN_MILLISECONDS) {
				this.props.createAppointment(event.slots[0]);
			}
		}
	}

	eventPropGetter = (event) => {
		let className = 'event-';

		event.color
			? (className += event.color)
			: (className += 'default');

		return { className };
	}

	onRangeChange = (range) => {
		if (!this.state.allowRequest) return;
		let dateStart;
		let dateEnd;
		if (Array.isArray(range)) {
			dateStart = range[0];
			dateEnd = moment(range[range.length - 1]).endOf('day').toDate();
		} else if (range.start && range.end) {
			dateStart = range.start;
			dateEnd = range.end;
		}
		this.props.setSchedulerSetting('currentDate', dateStart);
		this.setState({
			dateStart,
			dateEnd,
		});
		if (this.props.getAppointments) {
			this.props.getAppointments({
				from: dateStart,
				to: dateEnd,
			});
		}
	}

	setAllowRequest = (allowRequest) => new Promise((resolve) => {
		this.setState({ allowRequest }, resolve);
	});

	onDrillDown = async (day, view) => {
		await this.setAllowRequest(false);
		this.bigCalendar.current.handleNavigate(null, day);
		await this.setAllowRequest(true);
		this.bigCalendar.current.handleViewChange(view);
	}

	changeView = (view) => {
		this.props.setSchedulerSetting('view', view);
	}

	tooltipAccessor = ({ start, end }) => {
		const format = 'hh:mm A';
		return `${moment(start).format(format)} - ${moment(end).format(format)}`;
	}

	getEvents = () => {
		const {
			dateStart,
			dateEnd,
		} = this.state;

		const {
			view,
			contactCenter,
			appointments,
		} = this.props;

		return appointmentsMapping(appointments, view, dateStart, dateEnd, contactCenter);
	};

	render() {
		const { classes, currentDate } = this.props;
		return (
			<div className={classes.wrapper}>
				<BigCalendar
					ref={this.bigCalendar}
					selectable="ignoreEvents"
					localizer={localizer}
					events={this.getEvents()}
					defaultView='week'
					defaultDate={currentDate}
					onSelectEvent={this.onSelectEvent}
					onView={this.changeView}
					eventPropGetter={this.eventPropGetter}
					onRangeChange={this.onRangeChange}
					onDrillDown={this.onDrillDown}
					tooltipAccessor={null}
					components={{
						toolbar: this.state.toolbar,
						event: Event,
					}}
					scrollToTime={new Date(1970, 1, 1, 6)}
				/>
			</div>
		);
	}
}

const mapStateToProps = (state) => {
	const {
		view,
		currentDate,
		contactCenter,
		selectedUser,
		viewMode,
	} = state.common.schedulerSettings;
	return {
		view,
		currentDate,
		contactCenter,
		selectedUser,
		viewMode,
	};
};

const mapDispatchToProps = (dispatch) => ({
	setSchedulerSetting: bindActionCreators(setSchedulerSettingActionCreator, dispatch),
});

export default withStyles(styles)(connect(mapStateToProps, mapDispatchToProps)(Calendar));
